import { DEFAULTS, GRADE_EFFECT } from "./constants";
import type { Grade, ProblemStatus } from "./types";

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60 * 1000);
}

function addHours(date: Date, hours: number): Date {
  return new Date(date.getTime() + hours * 60 * 60 * 1000);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}

export interface ProblemStateInput {
  status: ProblemStatus;
  introducedAt: Date | null;
  memoryStrength: number;
  difficulty: number;
  successCount: number;
  failureCount: number;
  consecutiveCorrect: number;
  consecutiveWrong: number;
  recentAvgResponseMs: number | null;
  seenCountToday: number;
}

export interface UpdatedState {
  status: ProblemStatus;
  introducedAt: Date;
  lastSeenAt: Date;
  lastAnsweredAt: Date;
  dueAt: Date;
  memoryStrength: number;
  difficulty: number;
  successCount: number;
  failureCount: number;
  consecutiveCorrect: number;
  consecutiveWrong: number;
  recentAvgResponseMs: number;
  lastResponseMs: number;
  lastResult: "correct" | "wrong";
  lastGrade: Grade;
  seenCountToday: number;
}

/**
 * Normalize answer for comparison
 */
export function normalizeAnswer(input: string): string {
  return input.trim().toLowerCase();
}

/**
 * Determine grade based on correctness and response time
 */
export function determineGrade(wasCorrect: boolean, responseMs: number): Grade {
  if (!wasCorrect) {
    return "again";
  }

  if (responseMs <= DEFAULTS.easyResponseMs) {
    return "easy";
  } else if (responseMs <= DEFAULTS.hardResponseMs) {
    return "good";
  }
  return "hard";
}

/**
 * Check if answer is correct
 */
export function checkAnswer(userInput: string, correctAnswer: string): boolean {
  return normalizeAnswer(userInput) === normalizeAnswer(correctAnswer);
}

/**
 * Update problem state after a review
 */
export function updateStateAfterAnswer(
  state: ProblemStateInput,
  wasCorrect: boolean,
  responseMs: number,
  now: Date
): UpdatedState {
  const grade = determineGrade(wasCorrect, responseMs);
  const effect = GRADE_EFFECT[grade];

  // Calculate new memory strength
  let memoryStrength = clamp(
    state.memoryStrength +
      effect.deltaMemory +
      0.03 * Math.min(state.consecutiveCorrect, 3) -
      0.05 * state.consecutiveWrong,
    0.05,
    0.98
  );

  // Calculate new difficulty
  let difficulty = clamp(state.difficulty + effect.deltaDifficulty, 0.05, 0.95);

  // Update counters
  let successCount = state.successCount;
  let failureCount = state.failureCount;
  let consecutiveCorrect = state.consecutiveCorrect;
  let consecutiveWrong = state.consecutiveWrong;
  let status: ProblemStatus = state.status;
  let dueAt: Date;

  if (wasCorrect) {
    successCount += 1;
    consecutiveCorrect += 1;
    consecutiveWrong = 0;

    if (successCount < 2) {
      // Still in early learning
      status = "learning";
      if (grade === "hard") {
        dueAt = addHours(now, 12);
      } else if (grade === "good") {
        dueAt = addDays(now, 1);
      } else {
        dueAt = addDays(now, 2);
      }
    } else {
      // In review stage - calculate interval
      const baseIntervalDays =
        1 + 6 * memoryStrength * (1 - 0.6 * difficulty);

      let multiplier: number;
      if (grade === "hard") {
        multiplier = 1.2;
      } else if (grade === "good") {
        multiplier = 1.8;
      } else {
        multiplier = 2.5;
      }

      const nextIntervalDays = Math.max(0.5, baseIntervalDays * multiplier);
      dueAt = addDays(now, nextIntervalDays);
      status = consecutiveCorrect >= 2 ? "review" : "learning";
    }

    // Check for mastery promotion
    if (
      memoryStrength >= 0.85 &&
      consecutiveCorrect >= 4 &&
      difficulty <= 0.35
    ) {
      status = "mastered";
    }
  } else {
    // Wrong answer
    // Add extra difficulty penalty if failing after several correct
    if (state.consecutiveCorrect >= 2) {
      difficulty = clamp(difficulty + 0.04, 0.05, 0.95);
    }

    failureCount += 1;
    consecutiveWrong += 1;
    consecutiveCorrect = 0;
    status = "relearning";

    // Calculate retry delay
    const minutes = Math.min(30, 5 * consecutiveWrong);
    dueAt = addMinutes(now, Math.max(10, minutes));
  }

  // Calculate new average response time
  const recentAvgResponseMs =
    state.recentAvgResponseMs == null
      ? responseMs
      : 0.7 * state.recentAvgResponseMs + 0.3 * responseMs;

  return {
    status,
    introducedAt: state.introducedAt ?? now,
    lastSeenAt: now,
    lastAnsweredAt: now,
    dueAt,
    memoryStrength,
    difficulty,
    successCount,
    failureCount,
    consecutiveCorrect,
    consecutiveWrong,
    recentAvgResponseMs,
    lastResponseMs: responseMs,
    lastResult: wasCorrect ? "correct" : "wrong",
    lastGrade: grade,
    seenCountToday: state.seenCountToday + 1,
  };
}

/**
 * Create initial state for a newly introduced problem
 */
export function createInitialState(now: Date): Partial<UpdatedState> {
  return {
    status: "learning",
    introducedAt: now,
    memoryStrength: 0.15,
    difficulty: 0.5,
    confidence: 0.2,
    dueAt: now,
    successCount: 0,
    failureCount: 0,
    consecutiveCorrect: 0,
    consecutiveWrong: 0,
    seenCountToday: 0,
  } as Partial<UpdatedState> & { confidence: number };
}
